import { auth } from '../firebase';

const API_BASE_URL = 'https://europe-west1-medaviceia.cloudfunctions.net';

const RECORD_TYPES = {
  blood_pressure: { label: 'Tension artérielle', unit: 'mmHg', icon: '🫀', hasSecondValue: true, color: '#ef4444' },
  glucose: { label: 'Glycémie', unit: 'mg/dL', icon: '🩸', hasSecondValue: false, color: '#f59e0b' },
  weight: { label: 'Poids', unit: 'kg', icon: '⚖️', hasSecondValue: false, color: '#3b82f6' },
  temperature: { label: 'Température', unit: '°C', icon: '🌡️', hasSecondValue: false, color: '#10b981' },
  spo2: { label: 'Saturation O₂', unit: '%', icon: '💨', hasSecondValue: false, color: '#6366f1' },
  heart_rate: { label: 'Fréquence cardiaque', unit: 'bpm', icon: '❤️', hasSecondValue: false, color: '#ec4899' }
};

export const healthRecordService = {
  RECORD_TYPES,

  // Obtenir le token d'auth
  async getToken() {
    const user = auth.currentUser;
    if (!user) throw new Error('Non connecté');
    return user.getIdToken();
  },

  // Ajouter une mesure
  async addRecord(type, value, value2 = null, measuredAt = new Date(), notes = null) {
    const token = await this.getToken();

    const response = await fetch(`${API_BASE_URL}/saveHealthRecord`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        data: {
          type,
          value: parseFloat(value),
          value2: value2 ? parseFloat(value2) : null,
          unit: RECORD_TYPES[type]?.unit || "",
          measuredAt: measuredAt.toISOString(),
          notes
        }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de l\'ajout');
    }

    return response.json();
  },

  // Supprimer une mesure
  async deleteRecord(recordId) {
    const token = await this.getToken();

    const response = await fetch(`${API_BASE_URL}/deleteHealthRecord`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        data: { recordId } 
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de la suppression');
    }

    return response.json();
  },

  // Récupérer les mesures
  async getRecords(type = null, limit = 50) {
    const token = await this.getToken();

    const requestData = { limit };
    if (type) requestData.type = type;

    const response = await fetch(`${API_BASE_URL}/getHealthRecords`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ data: requestData })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de la récupération');
    }

    const responseData = await response.json();
    
    const records = responseData.data?.records || responseData.result?.records || responseData.records || [];

    return records.map(r => ({
      ...r,
      measuredAt: new Date(r.measuredAt)
    }));
  },

  // Récupérer par type
  async getRecordsByType(type, limit = 30) {
    return this.getRecords(type, limit);
  },

  // Dernière valeur par type
  async getLatestByType(type) {
    const records = await this.getRecordsByType(type, 1);
    return records[0] || null;
  },

  // Toutes les dernières valeurs (pour dashboard)
  async getLatestAll() {
    const latest = {};
    
    for (const type of Object.keys(RECORD_TYPES)) {
      try {
        latest[type] = await this.getLatestByType(type);
      } catch {
        latest[type] = null;
      }
    }

    return latest;
  },

  // Formater une valeur pour l'affichage
  formatValue(type, value, value2 = null) {
    const config = RECORD_TYPES[type];
    if (!config) return `${value}`;

    if (config.hasSecondValue && value2 !== null) {
      return `${value}/${value2} ${config.unit}`;
    }
    
    return `${value} ${config.unit}`;
  },

  // Formater la date
  formatDate(date) {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }
};